/**
 * Mobile UI — wires the simplified 2-deck + auto-mix mobile console
 * to the same AudioEngine, DeckControllers and MixAnalyzer as desktop.
 * Only active on touch devices in landscape orientation.
 */

const $ = id => document.getElementById(id)

export function initializeMobileUI({ engine, deckA, deckB, analyzer, playlistA, playlistB }) {
  // Only run on touch devices
  const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
  if (!isTouch) return

  // ── Visualizer canvases ──────────────────────────────────────────────────
  let vizACtx, vizBCtx
  const canvasA = $('m-waveform-a')
  const canvasB = $('m-waveform-b')
  if (canvasA) vizACtx = canvasA.getContext('2d')
  if (canvasB) vizBCtx = canvasB.getContext('2d')

  function drawWaveform(ctx, analyser, color) {
    if (!ctx || !analyser) return
    const W = ctx.canvas.width
    const H = ctx.canvas.height
    const data = new Uint8Array(analyser.frequencyBinCount)
    analyser.getByteTimeDomainData(data)
    ctx.clearRect(0, 0, W, H)
    ctx.strokeStyle = color
    ctx.lineWidth = 1.5
    ctx.beginPath()
    const step = W / data.length
    for (let i = 0; i < data.length; i++) {
      const y = ((data[i] / 128) - 1) * (H / 2) + H / 2
      i === 0 ? ctx.moveTo(0, y) : ctx.lineTo(i * step, y)
    }
    ctx.stroke()
  }

  function rafLoop() {
    drawWaveform(vizACtx, engine.analysers['A'], '#00ff88')
    drawWaveform(vizBCtx, engine.analysers['B'], '#00aaff')
    updateTimers()
    requestAnimationFrame(rafLoop)
  }
  rafLoop()

  // ── Time / progress updates ──────────────────────────────────────────────
  function fmt(s) {
    if (!s || isNaN(s)) return '0:00'
    const m = Math.floor(s / 60), sec = Math.floor(s % 60)
    return m + ':' + String(sec).padStart(2, '0')
  }

  function updateTimers() {
    updateDeckTimer(deckA, 'A')
    updateDeckTimer(deckB, 'B')

    // Compatibility score
    if (deckA.analysis && deckB.analysis) {
      const score = analyzer.scoreCompatibility(deckA.analysis, deckB.analysis, deckA.bpm, deckB.bpm)
      const el = $('mCompat')
      if (el) {
        el.textContent = score.toFixed(0) + '%'
        el.style.color = score > 70 ? 'var(--green)' : score > 40 ? 'var(--orange)' : 'var(--red)'
      }
    }
  }

  function updateDeckTimer(deck, id) {
    const ct = deck.currentTime || 0
    const dur = deck.duration || 0
    const rem = Math.max(0, dur - ct)
    const timeEl = $(`mTime${id}`)
    const remEl  = $(`mRem${id}`)
    const prog   = $(`mProgress${id}`)
    if (timeEl) timeEl.textContent = fmt(ct)
    if (remEl)  remEl.textContent  = '-' + fmt(rem)
    if (prog && dur > 0) prog.style.width = ((ct / dur) * 100).toFixed(1) + '%'
  }

  // ── Play buttons ─────────────────────────────────────────────────────────
  function syncMPlayBtn(id, deck) {
    const btn  = $(`mPlay${id}`)
    const icon = $(`mPlayIcon${id}`)
    if (!btn || !icon) return
    btn.classList.toggle('playing', deck.isPlaying)
    icon.innerHTML = deck.isPlaying ? '&#9646;&#9646;' : '&#9654;'
  }

  $('mPlayA')?.addEventListener('click', async () => {
    if (engine.ctx.state === 'suspended') await engine.ctx.resume()
    deckA.isPlaying ? deckA.pause() : deckA.play()
    syncMPlayBtn('A', deckA)
  })
  $('mPlayB')?.addEventListener('click', async () => {
    if (engine.ctx.state === 'suspended') await engine.ctx.resume()
    deckB.isPlaying ? deckB.pause() : deckB.play()
    syncMPlayBtn('B', deckB)
  })

  // ── CUE buttons ──────────────────────────────────────────────────────────
  $('mCueA')?.addEventListener('click', () => {
    if (!deckA.audioBuffer) return
    if (deckA.isPlaying) { deckA.pause(); deckA.seekTo(deckA.cuePoint || 0) }
    else { deckA.cuePoint = deckA.currentTime; deckA.play(); syncMPlayBtn('A', deckA) }
  })
  $('mCueB')?.addEventListener('click', () => {
    if (!deckB.audioBuffer) return
    if (deckB.isPlaying) { deckB.pause(); deckB.seekTo(deckB.cuePoint || 0) }
    else { deckB.cuePoint = deckB.currentTime; deckB.play(); syncMPlayBtn('B', deckB) }
  })

  // ── Sync buttons ─────────────────────────────────────────────────────────
  $('mSyncA')?.addEventListener('click', () => {
    if (!deckB.bpm || !deckA.bpm) return
    const ratio = deckB.bpm / deckA.bpm
    deckA.playbackRate = ratio
    if (deckA._source) deckA._source.playbackRate.value = ratio
    $('mSyncA').classList.toggle('synced', true)
    updateDeckMeta('A', deckA)
  })
  $('mSyncB')?.addEventListener('click', () => {
    if (!deckA.bpm || !deckB.bpm) return
    const ratio = deckA.bpm / deckB.bpm
    deckB.playbackRate = ratio
    if (deckB._source) deckB._source.playbackRate.value = ratio
    $('mSyncB').classList.toggle('synced', true)
    updateDeckMeta('B', deckB)
  })

  // ── Tempo sliders ────────────────────────────────────────────────────────
  $('mTempoA')?.addEventListener('input', e => {
    const rate = 1 + (parseFloat(e.target.value) / 100)
    deckA.playbackRate = rate
    if (deckA._source) deckA._source.playbackRate.value = rate
    $('mSyncA')?.classList.remove('synced')
  })
  $('mTempoB')?.addEventListener('input', e => {
    const rate = 1 + (parseFloat(e.target.value) / 100)
    deckB.playbackRate = rate
    if (deckB._source) deckB._source.playbackRate.value = rate
    $('mSyncB')?.classList.remove('synced')
  })

  // ── Crossfader ───────────────────────────────────────────────────────────
  $('mCrossfader')?.addEventListener('input', e => {
    engine.setCrossfader(parseFloat(e.target.value) / 100)
  })

  // ── Master volume ────────────────────────────────────────────────────────
  $('mMasterVol')?.addEventListener('input', e => {
    engine.setMasterVolume(parseFloat(e.target.value) / 100)
  })

  // ── Drop zones ───────────────────────────────────────────────────────────
  setupMobileDrop('mDropA', 'mFileA', deckA, 'A')
  setupMobileDrop('mDropB', 'mFileB', deckB, 'B')

  function setupMobileDrop(dropId, inputId, deck, deckId) {
    const drop  = $(dropId)
    const input = $(inputId)
    if (!drop || !input) return

    // Tap to open file picker
    drop.addEventListener('click', () => input.click())

    // File picker result
    input.addEventListener('change', async e => {
      const files = Array.from(e.target.files)
      if (files.length) await loadMobileTrack(files[0], deck, deckId)
      input.value = ''
    })

    // Drag-over
    drop.addEventListener('dragover', e => { e.preventDefault(); drop.classList.add('drag-over') })
    drop.addEventListener('dragleave', () => drop.classList.remove('drag-over'))
    drop.addEventListener('drop', async e => {
      e.preventDefault()
      drop.classList.remove('drag-over')
      const file = e.dataTransfer.files[0]
      if (file) await loadMobileTrack(file, deck, deckId)
    })
  }

  // ── Load track (mobile path) ─────────────────────────────────────────────
  async function loadMobileTrack(file, deck, deckId) {
    if (!file) return
    if (engine.ctx.state === 'suspended') await engine.ctx.resume()

    const drop = $(`mDrop${deckId}`)
    if (drop) {
      drop.querySelector('.m-drop__text').textContent = 'LOADING...'
    }

    try {
      const ab = await file.arrayBuffer()
      const audioBuffer = await engine.decodeAudio(ab)
      const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')

      deck.loadBuffer(audioBuffer, 128, name, '')
      deck.analysis = null

      // Update UI immediately
      updateDeckMeta(deckId, deck, name)
      if (drop) {
        drop.classList.add('has-track')
        drop.querySelector('.m-drop__icon').textContent = '✓'
        drop.querySelector('.m-drop__text').textContent = 'TAP TO CHANGE'
      }

      // Analyze in background (non-blocking)
      analyzer.analyzeTrack(audioBuffer, null).then(analysis => {
        if (!analysis) return
        deck.analysis = analysis
        deck.bpm = analysis.bpm || deck.bpm
        if (deck._source) deck._source.playbackRate.value = deck.playbackRate || 1
        updateDeckMeta(deckId, deck, name, analysis)

        // Also add to playlist
        const pl = deckId === 'A' ? playlistA : playlistB
        pl.addTrack({
          file: file.name, name, artist: '', bpm: analysis.bpm || 128,
          key: analysis.key || '-', energy: analysis.energy || 0,
          analysis, duration: audioBuffer.duration, audioBuffer
        })
      })

    } catch (err) {
      console.error('Mobile load error:', err)
      if (drop) drop.querySelector('.m-drop__text').textContent = 'ERROR — TAP TO RETRY'
    }
  }

  function updateDeckMeta(id, deck, name, analysis) {
    const nameEl = $(`mTrackName${id}`)
    const metaEl = $(`mMeta${id}`)
    const bpmEl  = $(`mBpm${id}`)
    if (nameEl && name) nameEl.textContent = name
    if (analysis) {
      const bpmStr = analysis.bpm ? analysis.bpm + ' BPM' : '-- BPM'
      const keyStr = analysis.key || '-'
      if (metaEl) metaEl.textContent = bpmStr + ' · ' + keyStr
      if (bpmEl)  bpmEl.textContent  = bpmStr
    }
  }

  // ── Auto-Mix (mobile) ────────────────────────────────────────────────────
  let autoMixInterval = null

  $('mAutoMix')?.addEventListener('click', async () => {
    if (engine.ctx.state === 'suspended') await engine.ctx.resume()
    const btn = $('mAutoMix')
    btn.classList.toggle('active')

    if (btn.classList.contains('active')) {
      // Start both decks if needed
      if (deckA.audioBuffer && !deckA.isPlaying) { deckA.play(); syncMPlayBtn('A', deckA) }
      autoMixInterval = startMobileAutoMix()
    } else {
      if (autoMixInterval) { clearInterval(autoMixInterval); autoMixInterval = null }
    }
  })

  function startMobileAutoMix() {
    let fading = false
    return setInterval(() => {
      if (fading) return
      let src, tgt, srcId, tgtId, xfTarget
      if (deckA.isPlaying && deckA.remaining <= 30 && deckA.remaining > 0) {
        src = deckA; tgt = deckB; srcId = 'A'; tgtId = 'B'; xfTarget = 1.0
      } else if (deckB.isPlaying && deckB.remaining <= 30 && deckB.remaining > 0) {
        src = deckB; tgt = deckA; srcId = 'B'; tgtId = 'A'; xfTarget = 0.0
      } else return

      fading = true

      // BPM match
      if (src.bpm && tgt.bpm) {
        const ratio = src.bpm / tgt.bpm
        if (Math.abs(ratio - 1) < 0.15) {
          tgt.playbackRate = ratio
          if (tgt._source) tgt._source.playbackRate.value = ratio
        }
      }

      if (tgt.audioBuffer && !tgt.isPlaying) { tgt.play(); syncMPlayBtn(tgtId, tgt) }

      const fadeDur = (src.analysis && tgt.analysis)
        ? analyzer.scoreCompatibility(src.analysis, tgt.analysis, src.bpm, tgt.bpm) > 60 ? 14 : 8
        : 10

      const startXf = parseFloat($('mCrossfader').value) / 100
      const startT  = engine.ctx.currentTime
      const tick = setInterval(() => {
        const t = Math.min((engine.ctx.currentTime - startT) / fadeDur, 1)
        const ease = t < 0.5 ? 2*t*t : 1 - Math.pow(-2*t+2,2)/2
        const val = startXf + (xfTarget - startXf) * ease
        engine.setCrossfader(val)
        $('mCrossfader').value = val * 100
        // Ease BPM back to natural
        if (tgt.playbackRate !== 1) {
          tgt.playbackRate = tgt.playbackRate + (1 - tgt.playbackRate) * t
          if (tgt._source) tgt._source.playbackRate.value = tgt.playbackRate
        }
        if (t >= 1) {
          clearInterval(tick)
          src.pause(); syncMPlayBtn(srcId, src)
          engine.setCrossfader(0.5); $('mCrossfader').value = 50
          tgt.playbackRate = 1; if (tgt._source) tgt._source.playbackRate.value = 1
          fading = false
        }
      }, 50)
    }, 1000)
  }
}
